"use client";

import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { DrawerDirection, Environment, Theme } from "../../../types";
import type { PreferredToken, TokenBalance, WalletInfoStyle } from "../types";
import type {
  EstimateGasFn,
  GetTransactionReceiptFn,
  LpBalanceReaderFn,
  PortfolioSection,
  SendTransactionFn,
  StakingRewardsReaderFn,
} from "../../wallet-portfolio/types";
import type { SendAccount, SendAsset } from "../../send";
import type { OnOutlink } from "../../../lib/outlink";
import type {
  BridgeApproveFn,
  BridgeHistoryItem,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeQuoteFn,
  BridgeSubmitFn,
  BridgeToken,
} from "../../bridge";
import type { OnePopSummary } from "../../onepop";

export type WalletInfoView =
  | "wallet"
  | "qr"
  | "portfolio"
  | "send"
  | "bridge"
  | "token-detail"
  | "onepop";

export interface WalletInfoContextValue {
  env: Environment;
  theme: Theme;
  isMobile: boolean;
  open: boolean;
  drawerDirection: DrawerDirection;
  modal: boolean;
  showBalance: boolean;
  showForgeToken: boolean;
  showGameToken: boolean;
  showQR: boolean;
  showBridge: boolean;
  qrLogoSrc?: string;
  walletAddress: string;
  accountName?: string;
  sendAccounts?: SendAccount[];
  profileImageUrl?: string;
  connectorName?: string;
  connectorIconUrl?: string;
  preferredTokens: PreferredToken[];
  close: () => void;
  onSelectWallet?: () => void;
  onCopyAddress?: (address: string, success: boolean) => void;
  onBuy?: () => void | Promise<void>;
  /**
   * Buy 카드를 시각적으로 비활성 상태로 표시하고, 사용자가 클릭하면 이 메시지를
   * 토스트로 띄운다. `onBuy`보다 우선. 예: on-ramp가 사용자 국가에서 차단된 경우.
   * `onBuy`가 reject된 경우(예: allow/url API 실패)에도 동일 토스트 메시지로 재사용.
   */
  onBuyDisabledMessage?: string;
  /**
   * Bridge 카드를 시각적으로 비활성 상태로 표시하고, 클릭하면 이 메시지를
   * 토스트로 띄운다. 브릿지 웹 이동보다 우선. 예: 연결된 지갑이 CROSSx
   * 계열이 아니라 브릿지를 지원하지 않는 경우.
   */
  onBridgeDisabledMessage?: string;
  /**
   * Send 카드(및 토큰 행 탭으로 진입하는 전송 플로우)를 시각적으로 비활성
   * 상태로 표시하고, 클릭하면 이 메시지를 토스트로 띄운다. `onSend` / 기본
   * 토큰 선택 진입보다 우선. 예: 연결된 지갑이 CROSSx 계열이 아닌 경우.
   */
  onSendDisabledMessage?: string;
  onDisconnect?: () => void;
  disconnectLabel: string;
  termsUrl?: string;
  termsLabel: string;
  privacyUrl?: string;
  privacyLabel: string;
  portalStyle?: WalletInfoStyle;
  navContent: ReactNode | null;
  navPosition: "top" | "bottom";
  setNavContent: (content: ReactNode | null) => void;
  setNavPosition: (position: "top" | "bottom") => void;
  footerContent: ReactNode | null;
  setFooterContent: (content: ReactNode | null) => void;
  view: WalletInfoView;
  setView: (view: WalletInfoView) => void;
  selectedSendToken: SendAsset | null;
  setSelectedSendToken: (token: SendAsset | null) => void;
  /** 토큰 상세/이력 화면에서 보여줄 토큰. 토큰 행 탭 시 설정된다. */
  selectedDetailToken: TokenBalance | null;
  setSelectedDetailToken: (token: TokenBalance | null) => void;
  showPortfolio: boolean;
  /**
   * 액션 행의 ONEpop 카드와 ONEpop 진입 화면 노출 여부. 기본 숨김(false).
   * 화면 자체는 순수 UI라 아래 데이터/콜백이 없어도 렌더된다 —
   * 액션 버튼은 ONEpop 서비스 딥링크가 기본 동작이다.
   */
  showOnePop: boolean;
  /**
   * ONEpop 화면의 상태(수령 대기 수/합계, replies 카운트, 최근 활동).
   * dapp-ui는 `@nexus-cross/pop`(viem 필수)에 의존할 수 없어 직접 조회하지
   * 않는다 — 호스트가 조회해 주입한다. 미주입 시 "내역 없음" 화면.
   */
  onePopSummary?: OnePopSummary;
  /**
   * ONEpop 뷰에 처음 진입할 때 1회 호출. 조회에 SIWE 서명이 필요해 지갑 팝업이
   * 뜨므로, 주입측이 이 시점까지 조회를 미룰 수 있게 하는 트리거다.
   */
  onOnePopOpen?: () => void;
  /** Send POP 카드 + "Send your first POP!" CTA. */
  onOnePopSend?: () => void;
  /** Claim POPs 카드 + 수령 대기 배너. */
  onOnePopClaim?: () => void;
  /** Activity 카드 + "See all activity" + replies 배너. */
  onOnePopActivity?: () => void;
  /**
   * 활동 화면으로 나가는 경로에서 추가로 호출되는 알림 훅. 목적지 이동을
   * 대체하지 않고, 답장 확인 처리(`repliesWaitingCount` 감소)에만 쓴다.
   */
  onOnePopRepliesSeen?: () => void;
  /** ONEpop 워드마크 이미지. 미지정 시 내장 SVG. */
  onePopGetTokenSlot?: React.ReactNode;
  onePopLogoSrc?: string;
  /** ONEpop 히어로 일러스트 이미지. 미지정 시 내장 SVG. */
  onePopHeroSrc?: string;
  portfolioTitle: string;
  /**
   * 노출할 포트폴리오 섹션 필터. 미지정 시 전체 노출, 배열 지정 시 포함된
   * 섹션만 노출. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  portfolioSections?: PortfolioSection[];
  showTotalAssets: boolean;
  totalAssetsLabel: string;
  /** Send 페이지의 일반 토큰 전송에 사용할 외부 트랜잭션 전송 함수. */
  sendTransaction?: SendTransactionFn;
  getTransactionReceipt?: GetTransactionReceiptFn;
  /**
   * Send 확인 단계에서 사용할 가스 추정 함수. SendPage로 그대로 전달된다.
   */
  estimateGas?: EstimateGasFn;
  /**
   * Terms/Privacy · 포트폴리오 섹션 등 외부 링크로 새 탭을 여는 모든 지점에서
   * 호출되는 가로채기 콜백. 반환값으로 URL 변형 / 취소가 가능하며 async도 지원.
   * 포트폴리오 영역은 `WalletPortfolioContext`로 그대로 릴레이된다.
   */
  onOutlink?: OnOutlink;
  /**
   * game-swap LP 섹션이 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션이 온체인 `earned()` / `balanceOf()`를 보강하기 위한
   * 주입형 reader. `showPortfolio=true`일 때 `WalletPortfolioBody`로 릴레이된다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  bridgeTokens?: BridgeToken[];
  bridgeHistory?: BridgeHistoryItem[];
  getBridgeQuote?: BridgeQuoteFn;
  getBridgeToTokens?: BridgeGetToTokensFn;
  getBridgeApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  submitBridge?: BridgeSubmitFn;
  /**
   * 상단 QR 버튼 / 기본 액션 row의 Receive / Send 콜백. (Buy는 위
   * onBuy/onBuyDisabledMessage로 정의됨.)
   */
  onReceive?: () => void;
  /**
   * @deprecated Bridge 버튼은 항상 apps.json(gametokenBridge) 웹으로
   * 이동한다. 이 콜백은 더 이상 호출되지 않는다.
   */
  onBridge?: () => void;
  onSend?: () => void;
}

export const WalletInfoContext = createContext<WalletInfoContextValue | null>(
  null,
);

export function useWalletInfoContext() {
  const ctx = useContext(WalletInfoContext);
  if (!ctx) {
    throw new Error(
      "WalletInfo compound components must be used within <WalletInfo>",
    );
  }
  return ctx;
}
